import { DailyReward, Stats } from '../types';

// UTC-normalized helpers to avoid timezone-induced streak breaks
const getUTCDateString = (): string => {
  const now = new Date();
  return `${now.getUTCFullYear()}-${String(now.getUTCMonth() + 1).padStart(2, '0')}-${String(now.getUTCDate()).padStart(2, '0')}`;
};

const getYesterdayUTCDateString = (): string => {
  const now = new Date();
  const y = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate() - 1));
  return `${y.getUTCFullYear()}-${String(y.getUTCMonth() + 1).padStart(2, '0')}-${String(y.getUTCDate()).padStart(2, '0')}`;
};

// Bonus rewards pool with weighted probabilities
const BONUS_REWARDS = [
  { type: 'xp_boost' as const, name: 'XP Boost', value: 50, icon: '⚡', weight: 25 },
  { type: 'xp_boost' as const, name: 'Large XP Boost', value: 100, icon: '💫', weight: 10 },
  { type: 'currency_boost' as const, name: 'Currency Bonus', value: 25, icon: '💰', weight: 25 },
  { type: 'currency_boost' as const, name: 'Large Currency Bonus', value: 50, icon: '💎', weight: 10 },
  { type: 'stat_boost' as const, name: 'Strength Boost', value: { strength: 2 }, icon: '⚔️', weight: 15 },
  { type: 'stat_boost' as const, name: 'Intelligence Boost', value: { intelligence: 2 }, icon: '🧠', weight: 15 },
];

// Weekly milestone rewards (every 7 days) - generated for scalability
const WEEKLY_MILESTONES = Array.from({ length: 4 }, (_, i) => ({
  day: (i + 1) * 7,
  name: `Week ${i + 1} Bonus`,
  xp: 100 + i * 50,
  currency: 50 + i * 25,
  icon: ['📅', '📆', '🗓️', '🏆'][i],
}));

export const dailyRewardService = {
  // Check if user should receive daily reward
  shouldReceiveDailyReward: (lastLoginDate: string): boolean => {
    const today = getUTCDateString();
    return lastLoginDate !== today;
  },

  // Calculate login streak
  calculateLoginStreak: (lastLoginDate: string, currentStreak: number): number => {
    const today = getUTCDateString();
    if (lastLoginDate === today) return currentStreak; // Already counted today
    const yesterday = getYesterdayUTCDateString();
    if (lastLoginDate === yesterday) return currentStreak + 1; // Continuing streak
    return 1; // Streak broken, reset to 1
  },

  // Calculate base daily reward (scales with streak)
  calculateBaseDailyReward: (streakDay: number): { xp: number; currency: number } => {
    // Base reward increases with streak
    const baseXP = 20;
    const baseCurrency = 10;
    
    // Bonus multiplier for longer streaks (caps at 2x for 30+ days)
    const streakMultiplier = Math.min(1 + (streakDay * 0.033), 2.0);
    
    return {
      xp: Math.floor(baseXP * streakMultiplier),
      currency: Math.floor(baseCurrency * streakMultiplier),
    };
  },

  // Random bonus reward (20% chance)
  getRandomBonusReward: (): DailyReward['bonusReward'] | undefined => {
    // 20% chance for bonus
    if (Math.random() > 0.2) return undefined;
    
    // Weighted random selection
    const totalWeight = BONUS_REWARDS.reduce((sum, reward) => sum + reward.weight, 0);
    let random = Math.random() * totalWeight;
    
    for (const reward of BONUS_REWARDS) {
      random -= reward.weight;
      if (random <= 0) {
        return {
          type: reward.type,
          name: reward.name,
          value: reward.value,
          icon: reward.icon,
        };
      }
    }
    
    return undefined;
  },

  // Check for weekly milestone
  getWeeklyBonus: (streakDay: number): DailyReward['weeklyBonus'] | undefined => {
    const milestone = WEEKLY_MILESTONES.find(m => m.day === streakDay);
    return milestone;
  },

  // Generate complete daily reward
  generateDailyReward: (lastLoginDate: string, currentStreak: number): DailyReward | null => {
    // Check if reward should be given
    if (!dailyRewardService.shouldReceiveDailyReward(lastLoginDate)) {
      return null;
    }
    
    // Calculate new streak
    const newStreak = dailyRewardService.calculateLoginStreak(lastLoginDate, currentStreak);
    
    // Calculate base reward
    const baseReward = dailyRewardService.calculateBaseDailyReward(newStreak);
    
    // Check for bonus reward
    const bonusReward = dailyRewardService.getRandomBonusReward();
    
    // Check for weekly milestone
    const weeklyBonus = dailyRewardService.getWeeklyBonus(newStreak);
    
    // Caller should persist lastLoginDate = getUTCDateString() after applying
    return {
      xp: baseReward.xp,
      currency: baseReward.currency,
      bonusReward,
      weeklyBonus,
      streakDay: newStreak,
    };
  },

  // Apply reward to profile (returns updated values)
  applyReward: (
    reward: DailyReward,
    currentXP: number,
    currentCurrency: number,
    currentStats: Stats
  ): {
    newXP: number;
    newCurrency: number;
    newStats: Stats;
    statPoints: number;
  } => {
    let newXP = currentXP + reward.xp;
    let newCurrency = currentCurrency + reward.currency;
    const newStats = { ...currentStats };
    let statPoints = 0;
    
    // Apply bonus reward
    if (reward.bonusReward) {
      if (reward.bonusReward.type === 'xp_boost') {
        newXP += reward.bonusReward.value as number;
      } else if (reward.bonusReward.type === 'currency_boost') {
        newCurrency += reward.bonusReward.value as number;
      } else if (reward.bonusReward.type === 'stat_boost') {
        // Convert stat boosts to stat points instead of auto-applying
        const statBoost = reward.bonusReward.value as Partial<Stats>;
        statPoints += Object.values(statBoost).reduce((sum: number, val: number) => sum + val, 0);
      }
    }
    
    // Apply weekly bonus
    if (reward.weeklyBonus) {
      newXP += reward.weeklyBonus.xp;
      newCurrency += reward.weeklyBonus.currency;
    }
    
    return { newXP, newCurrency, newStats, statPoints };
  },

  // Get today's date string (UTC)
  getTodayDateString: getUTCDateString,
};

